/* eslint-disable max-statements */
import * as math from 'mathjs';

import { Declaration, DeclarationGoods, TemplateType } from '@e-origin/shared';

import { updateDeclaration, useSaveGoodsItems } from '@hooks/declaration';
import { templateType } from '@utils';

export interface SaveMultipleGoodsParam {
  declaration: Declaration;
  updatedGoods: DeclarationGoods[];
  aiModuleMustRunAfterSave?: boolean;
  newPriceSaved?: number;
  storeSavedDeclaration?: boolean;
}

export interface SaveDeclarationDetailsParam {
  updatedDeclaration: Declaration;
  propertiesToSend: string[];
  updatedGoodItems?: DeclarationGoods[];
  aiModuleMustRunAfterSave?: boolean;
  newPriceSaved?: number;
  storeSavedDeclaration?: boolean;
}

const computeGoodShipmentItemsCalculations = (declaration: Declaration, updatedGoods: DeclarationGoods[]): Declaration => {
  const updatedDeclaration = { ...declaration };
  let updatedGrossMass = 0;
  let updatedTransportCost = 0;

  updatedDeclaration.goodsShipmentItems = updatedDeclaration.goodsShipmentItems?.map((item) => {
    const updatedGood = updatedGoods.find((good) => good._id === item._id);
    if (updatedGood) {
      if (updatedGood.goodsMeasure?.grossMass) {
        updatedGrossMass = math.add(math.bignumber(updatedGood.goodsMeasure.grossMass), updatedGrossMass) as number;
      }
      if (updatedGood.transportInsurance?.amount) {
        updatedTransportCost = math.add(math.bignumber(updatedGood.transportInsurance.amount), updatedTransportCost) as number;
      }
      return updatedGood;
    }
    if (item.goodsMeasure?.grossMass) {
      updatedGrossMass = math.add(math.bignumber(item.goodsMeasure.grossMass), updatedGrossMass) as number;
    }
    if (item.transportInsurance?.amount) {
      updatedTransportCost = math.add(math.bignumber(item.transportInsurance.amount), updatedTransportCost) as number;
    }
    if (updatedGoods[0].evalMethodAiHsCode?.hsCodeOk || updatedGoods[0].evalMethodAiHsCode?.suggestedHsCode?.toString()?.length) {
      return {
        ...item,
        evalMethodDescription: { ...updatedGoods[0].evalMethodDescription },
        evalMethodAiHsCode: {
          ...item.evalMethodAiHsCode,
          hsCodeOk: true,
          suggestedHsCode: !updatedGoods[0].evalMethodAiHsCode?.validatedHSCodeCheck ? updatedGoods[0].evalMethodAiHsCode?.suggestedHsCode : '',
          validatedHSCodeCheck: updatedGoods[0].evalMethodAiHsCode?.validatedHSCodeCheck,
        },
      };
    }
    return {
      ...item,
      evalMethodDescription: { ...updatedGoods[0].evalMethodDescription },
    };
  });
  if (updatedDeclaration.consignment) {
    updatedDeclaration.consignment = {
      ...updatedDeclaration.consignment,
      grossMass: updatedGrossMass.valueOf(),
      transportCost: { ...updatedDeclaration.consignment.transportCost, amount: updatedTransportCost.valueOf() },
    };
  }

  return updatedDeclaration;
};

export const useDeclarationService = () => {
  const saveGoodsItems = useSaveGoodsItems();

  const saveMultipleGoods = async ({
    declaration,
    updatedGoods,
    aiModuleMustRunAfterSave,
    newPriceSaved,
    storeSavedDeclaration = true,
  }: SaveMultipleGoodsParam): Promise<void> => {
    const updatedDeclaration = computeGoodShipmentItemsCalculations(declaration, updatedGoods);
    await saveDeclarationDetails({
      updatedDeclaration,
      propertiesToSend: templateType() === TemplateType.HIGH_VALUE_H1 ? ['consignment'] : ['consignment', 'goodsShipmentItems'],
      updatedGoodItems: updatedGoods,
      aiModuleMustRunAfterSave,
      newPriceSaved,
      storeSavedDeclaration,
    });
  };
  const saveDeclarationDetails = async ({
    updatedDeclaration,
    propertiesToSend = [],
    updatedGoodItems,
    aiModuleMustRunAfterSave,
    newPriceSaved,
    storeSavedDeclaration = true,
  }: SaveDeclarationDetailsParam): Promise<void> => {
    if (templateType() === TemplateType.HIGH_VALUE_H1) {
      const webMatchingDescription = updatedDeclaration.goodsShipmentItems?.every((goodsShipmentItem: any) => {
        return !!goodsShipmentItem.evalMethodDescription?.webMatchingDescription;
      });
      const hsDescriptionOK = updatedDeclaration.goodsShipmentItems?.every((goodsShipmentItem: any) => {
        return !!goodsShipmentItem.evalMethodAiHsCode?.hsCodeOk;
      });

      const updatedDeclarationToSend = {
        ...updatedDeclaration,
        goodShipmentGlobal: {
          ...updatedDeclaration.goodShipmentGlobal,
          webMatchingDescription,
          hsDescriptionOK,
        },
      };

      await updateDeclaration({ updatedDeclaration: updatedDeclarationToSend, propertiesToSend });

      if (updatedGoodItems?.length) {
        await saveGoodsItems.mutateAsync({
          updatedGoodItems,
          declarationId: updatedDeclaration._id,
          aiModuleMustRunAfterSave,
          newPriceSaved,
          storeSavedDeclaration,
        });
      }

      return;
    }
    await updateDeclaration({ updatedDeclaration, propertiesToSend });
  };

  return { saveMultipleGoods, saveDeclarationDetails };
};
